import React, { useState, useEffect, createContext } from 'react'
import axios from 'axios'
import { IntlShape } from 'react-intl'
import { notification } from 'antd'

import { CourseContextType, Course, NewCreatedCourse } from './types'
import { apiUrl } from '../../globals'
import { processPreviewPictures } from './utils'


interface CourseProviderProps {
  courseContext: CourseContextType
  setCourseContext: (course: CourseContextType) => void
  createCourse: (course: NewCreatedCourse) => Promise<Course>
  deleteCourse: (courseId: number, intl: IntlShape) => void
  deleteCoursePage: (courseId: number, pageId: number, intl: IntlShape) => void
  updateCourse: (updatedCourse: Course) => void
  saveCourse: (course: Course, intl: IntlShape) => Promise<void>
  publishCourse: (course: Course, intl: IntlShape) => Promise<void>
  children?: React.ReactNode
}

const initialCourseContext: CourseContextType = {
  courses: [],
  loading: true
}

export const CourseContext = createContext<CourseProviderProps>({
  courseContext: initialCourseContext,
  setCourseContext: () => void 0,
  createCourse: () => Promise.resolve({} as Course),
  deleteCourse: () => void 0,
  deleteCoursePage: () => void 0,
  updateCourse: () => void 0,
  saveCourse: () => Promise.resolve(),
  publishCourse: () => Promise.resolve(),
  children: null
})

export const CourseProvider = ({ children }: CourseProviderProps) => {
  const [ courseContext, setCourseContext ] = useState(initialCourseContext)

  useEffect(() => {
    const getCourses = async () => {
      axios.get(`${apiUrl}/courses`)
        .then((res) => {
          setCourseContext({
            courses: parsePageContent(res.data) as Course[],
            loading: false
          })
        })
        .catch((err) => console.log("Error fetching courses: ", err)) // TODO: notification

    }

    getCourses()
  }, [])

  /**
   * @param course takes in a locally created, empty course without an id
   * @returns the course with an id from the database
   */
  const createCourse = async (course: NewCreatedCourse) => {
    const courseWithPreviewPictures = processPreviewPictures(course)

    const newCourse = {
      ...courseWithPreviewPictures,
      pages: courseWithPreviewPictures.pages.map((page) => {
        return {
          ...page,
          content: JSON.stringify(page.content),
          previewPicture: undefined,
          previewPictureBase64: page.previewPicture
        }
      })
    }

    const courseFromDb = await axios.post(`${apiUrl}/courses`, newCourse)
      .then((res) => parsePageContent(res.data))
      .catch((err) => {
        console.log("Error creating course: ", err)

        return []
      })

    setCourseContext((prevContext: CourseContextType) => {
      const courses = prevContext.courses.concat(courseFromDb)

      return { ...prevContext, courses }
    })

    return courseFromDb as Course
  }
  
  const deleteCourse = async (courseId: number, intl: IntlShape) => {
    const courseExists = courseContext.courses.some((course: Course) => course.id === courseId)
  
    if (!courseExists) {
      console.error(`CourseContext: Course with id ${courseId} does not exist.`)
      return
    }
  
    await axios.delete(`${apiUrl}/courses/${courseId}`)
      .then(() => {
        setCourseContext({
          courses: courseContext.courses.filter(course => course.id !== courseId),
        })

        notification.success({
          message: intl.formatMessage({ id: 'notification.success' }),
          description: intl.formatMessage({ id: 'notification.success.courseDeleted' }),
          placement: 'bottomRight',
        })
      })
      .catch((err) => {
        notification.error({
          message: intl.formatMessage({ id: 'notification.error' }),
          description: intl.formatMessage({ id: 'notification.error.courseNotDeleted' }),
          placement: 'bottomRight',
        })
        console.log("Error deleting course: ", err)
      })
  }

  const deleteCoursePage = (courseId: number, pageNumber: number, intl: IntlShape) => {
    const course = courseContext.courses.find((course: Course) => course.id === courseId)
  
    if (!course) {
      console.error(`CourseContext: Course with id ${courseId} does not exist.`)
      return
    }
  
    if (pageNumber < 0 || pageNumber >= course.pages.length) {
      console.error(`CourseContext: Invalid page number ${pageNumber} for course id ${courseId}.`)
      return
    }
  
    const updatedPages = course.pages.filter((_, index) => index !== pageNumber)
  
    // Update the page numbers of the remaining pages
    updatedPages.forEach((page, index) => {
      page.pageNumber = index + 1
    })
  
    setCourseContext((prevContext) => ({
      ...prevContext,
      courses: prevContext.courses.map((course) =>
        course.id === courseId
          ? { ...course, pages: updatedPages, hasUnsavedChanges: true }
          : course
      ),
    }))
  
    notification.success({
      message: intl.formatMessage({ id: 'notification.success' }),
      description: intl.formatMessage({ id: 'notification.success.pageDeleted' }),
      placement: 'bottomRight',
    })
  }
  

  const updateCourse = (updatedCourse: Course) => {
    updatedCourse.hasUnsavedChanges = true

    setCourseContext({
      courses: [
        ...courseContext.courses.map(
          course => course.id === updatedCourse.id
            ? updatedCourse 
            : course
        )
      ]
    })
  }

  const saveCourse = async (course: Course, intl: IntlShape): Promise<void> => {
    course.hasUnsavedChanges = false

    // the API is super finicky about the previewPicture field (which is kind of obsolete by now because 'fromPdf' pictures are used differently).
    // still, in order to not break anything, base64 images are stored in previewPictureBase64. should be fixed some time in the future.
    const updatedCourse = {
      ...course,
      pages: course.pages.map((page) => {
        return {
          ...page,
          content: JSON.stringify(page.content),
          previewPicture: undefined,
          previewPictureBase64: page.previewPicture
        }
      })
    }

    axios.patch(`${apiUrl}/courses/${course.id}`, updatedCourse)
      .then((res) => {
        notification.success({
          message: intl.formatMessage({ id: 'notification.success' }),
          description: intl.formatMessage({ id: 'notification.success.courseSaved' }),
          placement: 'bottomRight',
        })
        
        setCourseContext({
          courses: [
            ...courseContext.courses.map(
              course => course.id === res.data.id
                ? res.data
                : course
            )
          ]
        })
      })
      .catch(() => {
        notification.error({
          message: intl.formatMessage({ id: 'notification.error' }),
          description: intl.formatMessage({ id: 'notification.error.courseNotSaved' }),
          placement: 'bottomRight',
        })
      })
  }


  const publishCourse = async (course: Course, intl: IntlShape): Promise<void> => {
    axios.put(`${apiUrl}/publish-course/${course.id}`, course)
      .then((res) => {
        const updatedCourse = res.data

        setCourseContext((prevContext) => ({
          ...prevContext,
          courses: prevContext.courses.map(course => course.id === course.id ? updatedCourse : course),
        }))

        notification.success({
          message: intl.formatMessage({ id: 'notification.success' }),
          description: intl.formatMessage({ id: 'notification.success.coursePublished' }),
          placement: 'bottomRight',
        })
      })
      .catch(() => {
        notification.error({
          message: intl.formatMessage({ id: 'notification.error' }),
          description: intl.formatMessage({ id: 'notification.error.courseNotPublished' }),
          placement: 'bottomRight',
        })
      })
  }

  return (
    <CourseContext.Provider value={{
      courseContext,
      setCourseContext,
      createCourse,
      deleteCourse,
      deleteCoursePage,
      updateCourse,
      saveCourse,
      publishCourse,
      children
    }}>
      {children}
    </CourseContext.Provider>
  )
}

/**
 * @description Parses the content of pages if necessary (if they're done with the Editor and not 'fromPdf'  )
 */
const parsePageContent = (input: Course | Array<Course>) => {
  const parseCourse = (course: Course) => ({
    ...course,
    pages: course.pages.map(page => ({
      ...page,
      content: typeof page.content === 'string'
        ? JSON.parse(page.content)
        : page.content
    }))
  })

  return Array.isArray(input)
    ? input.map(parseCourse)
    : parseCourse(input)
}
